import { Breadcrumbs, Callout, type BreadcrumbProps } from "@blueprintjs/core";
import { useEffect, useMemo } from "react";
import { setSelectedParentIdHierarchy } from "../../../../store/slices/slice-media";
import { useAppDispatch, useAppSelector } from "../../../../store/store";
import { MEDIA_ROOT_LABEL, useMediaFolders } from "./useMediaFolders";

type MediaGridBreadCrumbsProps = {
  disabled?: boolean;
};

export function MediaGridBreadCrumbs({
  disabled = false,
}: MediaGridBreadCrumbsProps) {
  const dispatch = useAppDispatch();
  const selectedParentIdHierarchy = useAppSelector(
    (state) => state.media.selectedParentIdHierarchy,
  );

  const { folders, rootFolder, loading, error } = useMediaFolders();

  const breadCrums: BreadcrumbProps[] = useMemo(
    () =>
      selectedParentIdHierarchy.map<BreadcrumbProps>((id, idx) => {
        const folder = folders.find((f) => f.id === id);
        const isRoot = idx === 0;
        const last = idx === selectedParentIdHierarchy.length - 1;

        return {
          current: last,
          disabled: last || loading || disabled,
          icon: isRoot ? "home" : "folder-open",
          onClick: () => {
            if (!last) {
              dispatch(
                setSelectedParentIdHierarchy(
                  selectedParentIdHierarchy.slice(0, idx + 1),
                ),
              );
            }
          },
          text: isRoot ? MEDIA_ROOT_LABEL : folder?.name,
        };
      }) ?? [],
    [disabled, dispatch, folders, loading, selectedParentIdHierarchy],
  );

  useEffect(
    function effectOnRootDataChanged() {
      if (rootFolder?.id) {
        dispatch(setSelectedParentIdHierarchy([rootFolder.id]));
      }
    },
    [dispatch, rootFolder?.id],
  );

  if (error) {
    return (
      <Callout intent="danger">Failed loading media folder structure.</Callout>
    );
  }

  return (
    <div className="px-3 py-1.5">
      <Breadcrumbs items={breadCrums} />
    </div>
  );
}
